// src/components/trading/hooks/strategy-form/useStrategyForm.tsx
import { useState, useCallback, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import {
    StrategyFormData, PredictionModel, Strategy, Symbol, Basket,
    ExitCondition, BacktestSettingsType, ModelType, ExecutionTimingSettings,
    RiskParameters,
    LSTMSettings, ARIMASettings, XGBoostSettings, StewieQuantSettings,
    StewieGPTSettings, MonteCarloSettings, DijkstraSettings
} from '../../types';
import { useSymbolSelection } from './useSymbolSelection';
import { useExitConditions } from './useExitConditions';
import { useBasketManager } from './useBasketManager';
import { MODEL_TYPES } from '@/components/trading/constants';

interface UseStrategyFormProps {
  onSubmit: (data: any) => void;
  initialData?: Partial<Strategy> | null;
}

// --- Completed Default Settings ---
const defaultExecutionTiming: ExecutionTimingSettings = {
    signalFrequency: 'daily_close',
    monitorFrequency: '5min',
    executeRthOnly: true,
    allowPrePostMarket: false,
};
const defaultRiskParameters: RiskParameters = {
    maxPositionSizePct: 0.10, // Default to 10%
    maxStrategyExposurePct: 100, // Store as whole number (100%)
    isVolWeightedRisk: true,
    allowDynamicExits: true,
    stopLossType: 'percentage',
    stopLossValue: '2', // Represents 2%
    profitTargetType: 'percentage',
    profitTargetValue: '10', // Represents 10%
    atrPeriod: 14, // Example default
    volatilityPeriod: 20, // Example default
    correlationThreshold: 0.7, // Added default
    zscoreThreshold: 2.0 // Added default
};
// --- End Default Settings ---

// --- Completed Mapping Functions ---
const mapOldTimingToNew = (oldDuration?: string, oldInterval?: string): ExecutionTimingSettings => {
    const newSettings = { ...defaultExecutionTiming };
    if (oldDuration === '1D') {
        const intervalMap: { [key: string]: ExecutionTimingSettings['signalFrequency'] } = {
            '5': '5min',
            '15': '15min',
            '30': '15min', // Map 30 to 15 for simplicity, or handle as needed
            '60': 'hourly',
        };
        newSettings.signalFrequency = intervalMap[oldInterval || '15'] || '15min';
        // Adjust monitor frequency based on signal frequency
        newSettings.monitorFrequency = newSettings.signalFrequency === '5min' ? '1min' : '5min';
    } else {
        newSettings.signalFrequency = 'daily_close';
        newSettings.monitorFrequency = 'matches_signal';
    }
    return newSettings;
};

const mapOldRiskToNew = (oldProfitTarget?: string, oldStopLoss?: string, oldIsDynamicRisk?: boolean): RiskParameters => {
    const newRisk = { ...defaultRiskParameters };
    newRisk.allowDynamicExits = oldIsDynamicRisk !== undefined ? oldIsDynamicRisk : true;
    if (!newRisk.allowDynamicExits) {
        if (oldProfitTarget) {
            // Store as percentage string
            newRisk.profitTargetValue = (parseFloat(oldProfitTarget) * 100).toString();
            newRisk.profitTargetType = 'percentage';
        }
        if (oldStopLoss) {
            // Store as percentage string
            newRisk.stopLossValue = (parseFloat(oldStopLoss) * 100).toString();
            newRisk.stopLossType = 'percentage';
        }
    }
    return newRisk;
};
// --- End Mapping Functions ---


export const useStrategyForm = (
  onSubmit: (data: any) => void,
  initialData?: Partial<Strategy> | null,
  isPaperMode: boolean = false
) => {
  const { toast } = useToast();

  // --- Completed Initial State Setup ---
  const [formData, setFormData] = useState<StrategyFormData>(() => {
    // Create reusable functions for date calculations to ensure consistency
    const getEndDate = (): string => {
      const today = new Date();
      // Ensure we don't use future dates - use current date
      return today.toISOString().split('T')[0];
    };
    
    const getStartDate = (daysBack: number = 365): string => {
      const today = new Date();
      // Set date back by specified number of days
      const startDate = new Date(today);
      startDate.setDate(today.getDate() - daysBack);
      return startDate.toISOString().split('T')[0];
    };
    
    const baseBacktestSettings: BacktestSettingsType = {
        mode: 'paper',
        provider: 'alpaca', // Changed default to alpaca for better data access
        startDate: getStartDate(), // Use consistent function instead of inline calculation
        endDate: getEndDate(),     // Use consistent function for current date
        environment: 'all',
        marketEnvironment: 'all',
        initialCapital: '100000',
        useRealTimeData: false,
        dataUpdateInterval: '60',
        includeCommission: false,
        commissionRate: '0',
        includeSlippage: false,
        slippageModel: 'fixed',
        slippageValue: '0',
        executionDelay: '0',
        timeframe: 'day', // Explicitly initialize timeframe here
    };
    let initialExecTiming = defaultExecutionTiming;
    let initialRiskParams = defaultRiskParameters;
    
    // Create a robust merged backtest settings object
    const initialBsFromProp = initialData?.backtestSettings || {} as Partial<BacktestSettingsType>;
    
    // Validate and fix any dates in initialData that might be in the future
    if (initialBsFromProp.startDate || initialBsFromProp.endDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      // Check and fix end date if it's in the future
      if (initialBsFromProp.endDate) {
        const propEndDate = new Date(initialBsFromProp.endDate);
        propEndDate.setHours(0, 0, 0, 0);
        
        if (propEndDate > today) {
          console.warn('Adjusting future end date to today:', initialBsFromProp.endDate);
          initialBsFromProp.endDate = getEndDate();
        }
      }
      
      // Check and fix start date if it's in the future
      if (initialBsFromProp.startDate) {
        const propStartDate = new Date(initialBsFromProp.startDate);
        propStartDate.setHours(0, 0, 0, 0);
        
        if (propStartDate > today) {
          console.warn('Adjusting future start date to one year ago:', initialBsFromProp.startDate);
          initialBsFromProp.startDate = getStartDate();
        }
      }
    }
    
    const mergedBacktestSettings: BacktestSettingsType = {
      ...baseBacktestSettings, // Apply base defaults first
      ...initialBsFromProp,    // Then override with any settings from initialData
      // Explicitly ensure crucial defaults if initialBsFromProp might be incomplete
      timeframe: initialData?.backtestSettings?.timeframe ?? baseBacktestSettings.timeframe,
      provider: initialData?.backtestSettings?.provider ?? baseBacktestSettings.provider,
    };

    if (initialData) {
        // Use existing structured data if present, otherwise map old fields
        initialExecTiming = initialData.executionTiming
            ? { ...defaultExecutionTiming, ...initialData.executionTiming }
            : mapOldTimingToNew(initialData?.duration ?? undefined, initialData?.intradayInterval ?? undefined);

        initialRiskParams = initialData.riskParameters
            ? { ...defaultRiskParameters, ...initialData.riskParameters }
            : mapOldRiskToNew(initialData?.profitTarget ?? undefined, initialData?.stopLoss ?? undefined, initialData?.isDynamicRisk ?? undefined);

        // Migrate isVolWeightedRisk if it exists in old params structure
        if ((initialData.params as any)?.isVolWeightedRisk !== undefined) {
            initialRiskParams.isVolWeightedRisk = (initialData.params as any).isVolWeightedRisk;
        }

        // Convert stored decimal profit/stop back to percentage string for initial display in the form
        if (initialRiskParams.profitTargetValue && initialRiskParams.profitTargetType === 'percentage') {
             const numVal = parseFloat(initialRiskParams.profitTargetValue);
             // --- FIX: Convert decimal (0.05) to percentage string ("5") ---
             if(!isNaN(numVal)) initialRiskParams.profitTargetValue = (numVal * 100).toString();
             // --- END FIX ---
        }
        if (initialRiskParams.stopLossValue && initialRiskParams.stopLossType === 'percentage') {
             const numVal = parseFloat(initialRiskParams.stopLossValue);
              // --- FIX: Convert decimal (0.02) to percentage string ("2") ---
             if(!isNaN(numVal)) initialRiskParams.stopLossValue = (numVal * 100).toString();
             // --- END FIX ---
        }
    }

    return {
        name: initialData?.name || '',
        description: initialData?.description || '',
        // --- FIX: Use derived engine initially if possible ---
        engine: initialData
              ? (initialData.engine && initialData.engine !== 'default')
                  ? initialData.engine
                  : (initialData.models && initialData.models.length > 0)
                      ? (initialData.models.length === 1 && initialData.models[0].type === 'stewie_quant' ? 'stewie_quant' : 'default') // Derive initial engine from models
                      : 'static' // Explicitly static if no models
              : 'default', // Default for new strategies (will be updated on submit)
        // --- END FIX ---
        allocatedCapital: initialData?.allocatedCapital?.toString() || '25000', // Default to $25,000 for new strategies
        riskLevel: initialData?.riskLevel || 'medium',
        executionTiming: initialExecTiming,
        riskParameters: initialRiskParams,
        symbols: '', // Input managed by useSymbolSelection
        useSentiment: initialData?.useSentiment || false,
        usePathfinder: initialData?.usePathfinder || false,
        // Explicitly ensure backtestSettings.mode is set to paper or live
        backtestSettings: {
          ...mergedBacktestSettings,
          // Ensure mode is set to paper or we can't display it
          mode: initialData?.backtestSettings?.mode || isPaperMode ? 'paper' : 'live',
        },
        // --- Initialize removed fields for safety, they'll be deleted on submit ---
        profitTarget: initialData?.profitTarget ?? '',
        stopLoss: initialData?.stopLoss ?? '',
        isDynamicRisk: initialData?.isDynamicRisk ?? true,
        // --- End Initialize removed fields ---
        exitConditions: '', // Managed by useExitConditions
        baskets: [], // Managed by useBasketManager
        // Explicitly set isActive flag to make sure it's defined
        isActive: initialData?.isActive !== undefined ? initialData.isActive : true,
      };
  });
  // --- End Initial State Setup ---

  // Effect to ensure timeframe is always set if form data is updated externally (e.g. by initialData prop change)
  useEffect(() => {
    if (initialData && initialData.backtestSettings && typeof initialData.backtestSettings.timeframe === 'undefined') {
      setFormData(prev => ({
        ...prev,
        backtestSettings: { 
          ...prev.backtestSettings, 
          timeframe: 'day', 
          provider: prev.backtestSettings?.provider || 'alpaca' 
        } as BacktestSettingsType
      }));
    }
  }, [initialData]);

  const [models, setModels] = useState<PredictionModel[]>(initialData?.models || []);


  const handleFormDataChange = useCallback((fieldOrChanges: string | Partial<StrategyFormData>, value?: any) => {
     if (typeof fieldOrChanges === 'string' && value !== undefined) {
       // Handle single field update with key-value pair
       setFormData(prev => {
         const changes = { [fieldOrChanges]: value } as Partial<StrategyFormData>;
         const updatedState = { ...prev, ...changes };
         return updatedState;
       });
     } else if (typeof fieldOrChanges === 'object') {
       // Handle object with multiple changes
       setFormData(prev => {
         const changes = fieldOrChanges;
         const updatedState = { ...prev, ...changes };
         // Ensure nested riskParameters are merged correctly
         if (changes.riskParameters) {
           updatedState.riskParameters = { ...(prev.riskParameters || defaultRiskParameters), ...changes.riskParameters, };
         }
         return updatedState;
       });
     }
   }, []);

  const handleExecutionTimingChange = useCallback((changes: Partial<ExecutionTimingSettings>) => {
    console.log('📊 handleExecutionTimingChange called with:', changes);
    setFormData(prev => {
      const newExecutionTiming = { 
        ...defaultExecutionTiming, // Start with defaults
        ...prev.executionTiming,   // Merge existing settings
        ...changes                 // Apply new changes
      };
      console.log('📊 Previous executionTiming:', prev.executionTiming);
      console.log('📊 New executionTiming:', newExecutionTiming);
      return { ...prev, executionTiming: newExecutionTiming };
    });
  }, []);

  // Dedicated handler for risk parameters
  const handleRiskParamsChange = useCallback((changes: Partial<RiskParameters>) => {
    setFormData(prev => ({
      ...prev,
      riskParameters: {
        ...(prev.riskParameters || defaultRiskParameters), // Ensure base exists
        ...changes,
      },
    }));
  }, []);


   const updateFormDataForModels = useCallback((changes: Partial<StrategyFormData>) => { handleFormDataChange(changes); }, [handleFormDataChange]);

   // --- Completed Model Handlers ---
   const handleAddModel = useCallback((modelType: ModelType) => {
       const hasStewieQuant = models.some(m => m.type === 'stewie_quant');

       if (modelType === 'stewie_quant' && models.length > 0) {
           toast({ title: "Constraint", description: "StewieQuant must be the only model selected.", variant: "destructive" });
           return;
       }
       if (hasStewieQuant && modelType !== 'stewie_quant') {
           toast({ title: "Constraint", description: "StewieQuant cannot be combined with other models.", variant: "destructive" });
           return;
       }
       if (models.length >= 3 && modelType !== 'stewie_quant') {
           toast({ title: "Limit Reached", description: "You can add a maximum of 3 standard models.", variant: "default" });
           return;
       }

       let defaultSettings = {};
       switch (modelType) {
           case 'lstm': defaultSettings = { lstmSettings: { lookbackWindow: 60, predictionHorizon: 5, numLayers: 2, numUnits: 50, dropoutRate: 0.2, epochs: 50, batchSize: 32 } satisfies LSTMSettings }; break;
           case 'arima': defaultSettings = { arimaSettings: { p: 5, d: 1, q: 0, seasonal: false, P: 1, D: 1, Q: 1, m: 12 } satisfies ARIMASettings }; break;
           case 'xgboost': defaultSettings = { xgboostSettings: { n_estimators: 100, learning_rate: 0.1, max_depth: 6, subsample: 0.8, colsample_bytree: 0.8, gamma: 0, objective: 'reg:squarederror' } satisfies XGBoostSettings }; break;
           case 'stewie_quant': defaultSettings = { 
             stewieQuantSettings: { 
               optimizerLevel: 2, 
               adaptiveThreshold: 0.5,
               correlationThreshold: 0.7, 
               zscoreThreshold: 2.0, 
               reversionHalfLife: 10 
             } satisfies StewieQuantSettings 
           }; if (formData.usePathfinder) { updateFormDataForModels({ usePathfinder: false }); } break;
           case 'stewiegpt': defaultSettings = { stewieGPTSettings: { confidenceThreshold: 0.6, temperature: 0.3, maxOutputTokens: 256, newsSourceFilter: [], volatilityThreshold: 0.25 } satisfies StewieGPTSettings }; break;
           case 'monte_carlo': defaultSettings = { monteCarloSettings: { simulationCount: 10000, timeHorizon: 30, distribution: 'normal', includeTailRisks: false, confidenceInterval: 0.95, volShockEnabled: false, volShockMagnitude: 1.5 } satisfies MonteCarloSettings }; break;
           case 'dijkstra': defaultSettings = { dijkstraSettings: { pathOptimization: 'combined', riskWeight: 0.6, returnWeight: 0.4, includeFees: true, maxIterations: 1000 } satisfies DijkstraSettings }; break;
           case 'ensemble': defaultSettings = {}; break;
           case 'rl_dqn': defaultSettings = { 
             rlDqnSettings: { 
               timesteps: 15000,
               learning_rate: 0.001, 
               gamma: 0.95, 
               risk_aversion_param: 0.1,
               generate_signal_with_conviction: false,
               buy_conviction_threshold: 0.1,
               sell_conviction_threshold: 0.1,
               exploration_rate: 0.1,
               memory_size: 10000,
               batch_size: 32
             } 
           }; break;
           default: console.warn(`No default settings defined for model type: ${modelType}`); break;
       }

       const newModel: PredictionModel = {
           type: modelType,
           weight: 1,
           decisionControl: 'signal_generation',
           ...defaultSettings
       };

       if (modelType === 'stewie_quant') {
           setModels([newModel]);
           toast({ title: "Model Added", description: "StewieQuant Algorithm added. Other models removed." });
       } else {
           setModels(prev => [...prev, newModel]);
           toast({ title: "Model Added", description: `${MODEL_TYPES.find(m => m.id === modelType)?.name || modelType} added to strategy.` });
       }
   }, [models, formData.usePathfinder, updateFormDataForModels, toast]);

   const handleRemoveModel = useCallback((index: number) => {
       setModels(prev => prev.filter((_, i) => i !== index));
   }, []);

   const handleUpdateModel = useCallback((index: number, changes: Partial<PredictionModel>) => {
       const currentModel = models[index];
       const newType = changes.type;

       if (newType === 'stewie_quant' && models.length > 1 && currentModel?.type !== 'stewie_quant') {
           toast({ title: "Constraint", description: "StewieQuant must be the only model.", variant: "destructive"});
           return;
       }
       if (newType && newType !== 'stewie_quant' && models.some((m, i) => i !== index && m.type === 'stewie_quant')) {
           toast({ title: "Constraint", description: "StewieQuant cannot be combined with other models.", variant: "destructive"});
           return;
       }

       setModels(prev => {
           // Handle the case where StewieQuant replaces the array
           if (changes.type === 'stewie_quant' && prev[index]?.type !== 'stewie_quant') {
               if (formData.usePathfinder) {
                   updateFormDataForModels({ usePathfinder: false });
               }
               const updatedModel: PredictionModel = {
                   type: 'stewie_quant',
                   weight: 1,
                   stewieQuantSettings: { 
                     optimizerLevel: 2, 
                     adaptiveThreshold: 0.5,
                     correlationThreshold: 0.7,
                     zscoreThreshold: 2.0,
                     reversionHalfLife: 10,
                     ...(changes.stewieQuantSettings || {}) 
                   },
                   decisionControl: (changes.decisionControl as PredictionModel['decisionControl']) || 'signal_generation'
               };
               return [updatedModel]; // Return an array with only StewieQuant
           }

           // Handle updates within the existing array
           return prev.map((model, i) => {
               if (i === index) {
                   let updatedModel = { ...model, ...changes };

                   // Clear old settings if type changes away from StewieQuant
                   if (model.type === 'stewie_quant' && changes.type && changes.type !== 'stewie_quant') {
                       updatedModel.stewieQuantSettings = undefined;
                       // Add default settings for the new type if needed
                       if (changes.type === 'lstm') updatedModel.lstmSettings = { lookbackWindow: 60, predictionHorizon: 5, numLayers: 2, numUnits: 50, dropoutRate: 0.2, epochs: 50, batchSize: 32, ...changes.lstmSettings };
                       // Add others as needed
                   }

                   // Deep merge settings - ensure previous settings are kept if not updated
                   if (changes.monteCarloSettings) updatedModel.monteCarloSettings = { ...(model.monteCarloSettings || {}), ...changes.monteCarloSettings };
                   if (changes.dijkstraSettings) updatedModel.dijkstraSettings = { ...(model.dijkstraSettings || {}), ...changes.dijkstraSettings };
                   if (changes.stewieQuantSettings) updatedModel.stewieQuantSettings = { 
                     ...(model.stewieQuantSettings || {
                       optimizerLevel: 2,
                       adaptiveThreshold: 0.5,
                       correlationThreshold: 0.7,
                       zscoreThreshold: 2.0,
                       reversionHalfLife: 10
                     }),
                     ...changes.stewieQuantSettings 
                   };
                   if (changes.lstmSettings) updatedModel.lstmSettings = { ...(model.lstmSettings || {}), ...changes.lstmSettings };
                   if (changes.stewieGPTSettings) updatedModel.stewieGPTSettings = { ...(model.stewieGPTSettings || {}), ...changes.stewieGPTSettings };
                   if (changes.arimaSettings) updatedModel.arimaSettings = { ...(model.arimaSettings || {}), ...changes.arimaSettings };
                   if (changes.xgboostSettings) updatedModel.xgboostSettings = { ...(model.xgboostSettings || {}), ...changes.xgboostSettings };

                   return updatedModel;
               }
               return model;
           });
       });
   }, [models, formData.usePathfinder, updateFormDataForModels, toast]);
   // --- End Model Handlers ---

  // --- Uncomment Sub-hook Calls ---
  const {
    symbolInput, symbolDirection, symbols, setSymbolInput, setSymbolDirection,
    handleBasketSelect, handleAddSymbol, handleRemoveSymbol,
  } = useSymbolSelection(initialData?.symbols);

  const {
    exitConditions, 
    addExitCondition, 
    removeExitCondition, 
    updateExitCondition
  } = useExitConditions(initialData?.exitConditions);

  const {
    baskets, handleAddBasket, handleRemoveBasket, handleUpdateBasket
  } = useBasketManager(initialData?.baskets);
  // --- End Sub-hook Calls ---


  const handleBacktestSettingsChange = useCallback((newBacktestSettings: Partial<BacktestSettingsType>) => {
    // Ensure we have a complete set of backtest settings
    setFormData(prev => ({
      ...prev,
      backtestSettings: { 
        ...(prev.backtestSettings || {}),
        ...newBacktestSettings,
        // Keep the provider if specified, otherwise maintain current provider or fall back to alpaca
        provider: newBacktestSettings.provider || prev.backtestSettings?.provider || 'alpaca',
        timeframe: newBacktestSettings.timeframe || prev.backtestSettings?.timeframe || 'day' // Ensure timeframe is always set
      } as BacktestSettingsType
    }));
  }, [setFormData]);

  const handleRunBacktest = useCallback(() => {
    // Trigger saving strategy record with backtest status
    onSubmit({
      ...formData,
      backtestSettings: formData.backtestSettings,
      // ensure status is set to 'backtest' so PortfolioOverview picks it up
      status: 'backtest',
    });
  }, [formData, onSubmit]);

  // --- Update handleSubmit ---
  const handleSubmit = useCallback((e?: React.FormEvent) => {
    if (e) e.preventDefault();

    const riskParamsToSubmit = { ...(formData.riskParameters || defaultRiskParameters) };

    // Convert percentage strings back to decimals before submission
    if (riskParamsToSubmit.profitTargetValue && riskParamsToSubmit.profitTargetType === 'percentage') {
        const numericVal = parseFloat(riskParamsToSubmit.profitTargetValue);
        // --- FIX: Store as decimal string ---
        riskParamsToSubmit.profitTargetValue = !isNaN(numericVal) ? (numericVal / 100).toString() : '';
        // --- END FIX ---
    }
    if (riskParamsToSubmit.stopLossValue && riskParamsToSubmit.stopLossType === 'percentage') {
        const numericVal = parseFloat(riskParamsToSubmit.stopLossValue);
         // --- FIX: Store as decimal string ---
        riskParamsToSubmit.stopLossValue = !isNaN(numericVal) ? (numericVal / 100).toString() : '';
         // --- END FIX ---
    }
    // --- Ensure maxStrategyExposurePct is a number ---
     if (riskParamsToSubmit.maxStrategyExposurePct) {
         riskParamsToSubmit.maxStrategyExposurePct = parseInt(String(riskParamsToSubmit.maxStrategyExposurePct), 10) || 100;
     }
     // --- Ensure maxPositionSizePct is stored as decimal ---
     // It should already be a decimal from the state update, but double-check
      if (typeof riskParamsToSubmit.maxPositionSizePct === 'string') {
         const numericVal = parseFloat(riskParamsToSubmit.maxPositionSizePct);
         riskParamsToSubmit.maxPositionSizePct = !isNaN(numericVal) ? numericVal : 0.10; // Default if invalid
      } else if (riskParamsToSubmit.maxPositionSizePct === undefined || riskParamsToSubmit.maxPositionSizePct === null) {
         riskParamsToSubmit.maxPositionSizePct = 0.10; // Default if missing
      }

    // --- FIX: Dynamically set engine based on models ---
    let determinedEngine = 'static'; // Default if no models
    if (models.length > 0) {
        if (models.length === 1 && models[0].type === 'stewie_quant') {
            determinedEngine = 'stewie_quant';
        } else {
            // Has models, but not *only* StewieQuant
            determinedEngine = 'default';
        }
    }
    // --- END FIX ---

    // Make sure maxStrategyExposurePct is set as a number and not a decimal
    if (riskParamsToSubmit.maxStrategyExposurePct) {
      riskParamsToSubmit.maxStrategyExposurePct = parseInt(String(riskParamsToSubmit.maxStrategyExposurePct), 10) || 100;
    } else {
      riskParamsToSubmit.maxStrategyExposurePct = 100; // Default value
    }

    const finalStrategyData: Partial<Strategy> = {
      id: initialData?.id || Date.now().toString(),
      name: formData.name,
      description: formData.description,
      engine: determinedEngine, // Use the dynamically determined engine
      riskLevel: formData.riskLevel,
      allocatedCapital: parseFloat(formData.allocatedCapital) || 25000, // Convert string to number for storage
      executionTiming: formData.executionTiming,
      symbols: symbols, // Use symbols from the hook
      riskParameters: riskParamsToSubmit, // Use the converted values
      isActive: initialData?.isActive ?? true,
      useSentiment: formData.useSentiment,
      usePathfinder: formData.usePathfinder,
      // Keep other params if they exist and are not migrated, and merge in any new custom parameters
      params: {
          ...(initialData?.params || {}),
          // Preserve any custom parameters that were configured during strategy creation
          // These might come from specialized components like StewieQuantStrategyConfig
          ...(formData.params || {}),
      },
      models: models,
      exitConditions: exitConditions, // Use exitConditions from the hook
      baskets: baskets, // Use baskets from the hook
      backtestSettings: {
        ...(formData.backtestSettings!),
        mode: isPaperMode ? 'paper' : 'live', // Explicitly set mode here
      },
    };

    // --- Remove old/migrated top-level fields before submission ---
    // These are kept in the type definition for backward compatibility
    // But we prevent them from being serialized in new strategies
    delete (finalStrategyData as any).profitTarget;
    delete (finalStrategyData as any).stopLoss;
    delete (finalStrategyData as any).isDynamicRisk;
    delete (finalStrategyData.params as any)?.isVolWeightedRisk; // Remove migrated param
    // --- End Remove ---


    // --- Completed Cleanup Logic ---
    Object.keys(finalStrategyData).forEach(key => {
       const typedKey = key as keyof Strategy;
       if (finalStrategyData[typedKey] === undefined) { delete finalStrategyData[typedKey]; }
       if (key === 'params' && Object.keys(finalStrategyData.params || {}).length === 0) { delete finalStrategyData.params; }
       if (key === 'riskParameters' && Object.keys(finalStrategyData.riskParameters || {}).length === 0) { delete finalStrategyData.riskParameters; }
    });
    // --- End Cleanup Logic ---

    console.log("Submitting final strategy data:", finalStrategyData);
    // Include trading status: 'paper' when in paper mode, otherwise 'active'
    const strategyDataWithStatus = {
      ...finalStrategyData,
      // Ensure backtestSettings.mode is also correctly set on the object passed to onSubmit
      backtestSettings: {
        ...(finalStrategyData.backtestSettings!),
        mode: isPaperMode ? 'paper' : 'live', // Redundant if already set in finalStrategyData, but ensures consistency
      },
      status: isPaperMode ? 'paper' : 'active', // This top-level status might be used elsewhere
    };
    onSubmit(strategyDataWithStatus);
  }, [formData, symbols, models, exitConditions, baskets, onSubmit, initialData, isPaperMode]);
  // --- End update handleSubmit ---

  return {
    formData,
    symbolInput, symbolDirection, symbols, setSymbolInput, setSymbolDirection,
    handleBasketSelect, handleAddSymbol, handleRemoveSymbol,
    models, handleAddModel, handleRemoveModel, handleUpdateModel,
    exitConditions, 
    addExitCondition, 
    removeExitCondition, 
    updateExitCondition,
    baskets,
    handleAddBasket, 
    handleRemoveBasket, 
    handleUpdateBasket,
    handleFormDataChange, handleExecutionTimingChange, handleRiskParamsChange, 
    handleBacktestSettingsChange, handleRunBacktest, handleSubmit
  };
};
